<script setup>
import OrderCard from 'dashboard/components-next/Order/OrderCard.vue';
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import OrderDetails from 'dashboard/components-next/Order/OrderDetails.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WithdrawDialog from 'dashboard/components-next/Order/WithdrawDialog.vue';
import { useEmitter } from 'dashboard/composables/emitter';
import { useAlert } from 'dashboard/composables';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper.js';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import ReportsFiltersDateRange from '../../routes/dashboard/settings/reports/components/Filters/DateRange.vue';
import { DATE_RANGE_OPTIONS } from '../../routes/dashboard/settings/reports/constants';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from "date-fns/subDays";

const { currentChat } = defineProps({
  currentChat: {
    type: Object,
    default: null,
  },
});

const store = useStore();
// 从 Store 中获取 UI 状态
const uiFlags = useMapGetter('giftCards/getUIFlags');
const fetchingOrderList = computed(() => uiFlags.value.fetchingOrderList);
const fetchingOrderDetails = computed(() => uiFlags.value.fetchingOrderDetails);
const isOrdersLoaded = ref(false);
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');

const customDateRange = computed(() => {
  return [new Date(), new Date()];
});

const selectedDateRange = ref(DATE_RANGE_OPTIONS.LAST_7_DAYS);

const isDateRangeSelected = computed(() => {
  return selectedDateRange.value.id === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id;
});

const to = computed(() => {
  if (isDateRangeSelected.value) {
    return getUnixEndOfDay(customDateRange.value[1]);
  }
  return getUnixEndOfDay(new Date());
});

const from = computed(() => {
  if (isDateRangeSelected.value) {
    return getUnixStartOfDay(customDateRange.value[0]);
  }

  const { offset } = selectedDateRange.value;
  const fromDate = subDays(new Date(), offset);
  return getUnixStartOfDay(fromDate);
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const showWithdrawButton = computed(() => {
  return !hasPermissions(['encrypt_message'], userPermissions.value);
});

// 动态获取订单列表
const orders = computed(() => {
  return store.getters['giftCards/getOrderList'];
});

const fetchOrderList = async () => {
  await store.dispatch('giftCards/getOrderList', {
    conversation_id: currentChat.id,
    from: from.value,
    to: to.value,
  });
  isOrdersLoaded.value = true; // 标记订单已加载
};

const orderDetailsDialogRef = ref(null);
const orderWithdrawDialogRef = ref(null);

// 页面加载时获取订单列表
onMounted(() => {
  fetchOrderList();
});

const orderRef = ref([]);
// 点击订单卡片时的回调函数
const handleCardClick = order => {
  // await store.dispatch('giftCards/getOrderDetails', {
  //   id: order.id,
  // });
  console.log('order_id: ', order);
  orderRef.value = order;
  orderDetailsDialogRef.value.open();
};

const orderDetails = ref(null);
const showWithdrawDialogValue = ref(false);

const orderMessage = ref(null);
const handleWithdraw = order => {
  orderDetails.value = order;
  showWithdrawDialogValue.value = true;
  orderMessage.value = null;
  nextTick(() => {
    orderWithdrawDialogRef.value?.withdrawDialogRef.open();
  });
};

const showWithdrawDialog = computed(() => {
  return showWithdrawDialogValue.value === true && orderDetails.value != null;
});

const showOrderDetails = computed(() => {
  return orderRef.value != null;
});

const withdrawableOrder = computed(() => {
  // 遍历 orders，找到第一个 can_withdraw 为 true 的订单
  return orders.value.find(order => order.can_withdraw === true) || null;
});

const onCustomDateRangeChange = () => {
  fetchOrderList();
};

const onDateRangeChange = selectedRange => {
  selectedDateRange.value = selectedRange;
  fetchOrderList();
};

const handleSubmit = {};

const closeWithdrawDialog = () => {
  showWithdrawDialogValue.value = false;
  orderMessage.value = null;
};
watch(
  () => currentChat, // 监听的目标
  (newVal, oldVal) => {
    if (newVal && newVal.id !== oldVal?.id) {
      fetchOrderList();
    }
  },
  { immediate: false }
);
useEmitter('doGiftCardWithdraw', async message => {
  await fetchOrderList();
  // 等待订单加载完成
  if (!isOrdersLoaded.value) {
    while (!isOrdersLoaded.value) {
      await new Promise(resolve => {
        setTimeout(resolve, 50);
      }); // 每 50ms 检查一次
    }
  }
  if (withdrawableOrder.value != null) {
    orderDetails.value = withdrawableOrder.value;
    showWithdrawDialogValue.value = true;
    orderMessage.value = message;
    nextTick(() => {
      orderWithdrawDialogRef.value?.withdrawDialogRef.open();
    });
  } else {
    useAlert('No order to payout');
  }
});
</script>

<template>
  <div class="flex flex-col gap-4 pt-2">
    <div
      class="w-full grid gap-y-2 gap-x-1.5 grid-cols-[repeat(auto-fill,minmax(250px,1fr))]"
    >
      <ReportsFiltersDateRange @on-range-change="onDateRangeChange" />
      <WootDateRangePicker
        v-if="isDateRangeSelected"
        show-range
        class="no-margin auto-width"
        :value="customDateRange"
        :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        @change="onCustomDateRangeChange"
      />
    </div>
    <div
      v-if="fetchingOrderList"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <!-- 显示订单卡片 -->
    <template v-else-if="orders && orders.length">
      <OrderCard
        v-for="order in orders"
        :key="order.id"
        :number="order.order_no"
        :status="order.status"
        :amount="order.purchase_amount"
        :created-at="order.create_time"
        :currency-symbol="order.currency_symbol"
        :can-withdraw="order.can_withdraw && showWithdrawButton"
        @details="handleCardClick(order)"
        @withdraw="handleWithdraw(order)"
      />
    </template>

    <!-- 显示无订单数据 -->
    <div v-else class="text-center text-gray-500">No orders found.</div>
  </div>
  <Dialog
    v-if="showOrderDetails"
    ref="orderDetailsDialogRef"
    :is-loading="fetchingOrderDetails"
    :show-confirm-button="false"
    :show-cancel-button="false"
    type="edit"
    overflow-y-auto
    width="3xl"
    @confirm="handleSubmit"
  >
    <OrderDetails :id="orderRef.id" />
  </Dialog>

  <WithdrawDialog
    v-if="showWithdrawDialog"
    ref="orderWithdrawDialogRef"
    :current-chat="currentChat"
    :order="orderDetails"
    :message="orderMessage"
    @close="closeWithdrawDialog"
  />
</template>
